import React, { useState, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useTranslation } from 'react-i18next';
import { photoFileExtension } from '../utils/helpers';
import { formatFileSize } from '../utils/formatFileSize';

interface UploadedFile {
  file: File;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  errorMessage?: string;
}

interface ImageUploadModalProps {
  show: boolean;
  onClose: () => void;
  onAttach: (files: File[]) => void;
  maxFiles?: number;
  maxSizeMB?: number;
}

const ImagePicker: React.FC<ImageUploadModalProps> = ({
  show,
  onClose,
  onAttach,
  maxFiles = 5,
  maxSizeMB = 5,
}) => {
  const { t } = useTranslation(['translation', 'dynamic']);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return `File size is above ${maxSizeMB}MB`;
    }

    // #66: same rule as the upload (name first, MIME fallback), so an iPhone
    // HEIC with an empty MIME type is accepted
    if (photoFileExtension(file) === null) {
      return 'Invalid file type. Please upload PNG, JPG, WEBP or HEIC';
    }

    return null;
  };

  const simulateUpload = (file: File, index: number) => {
    const error = validateFile(file);

    if (error) {
      setUploadedFiles(prev =>
        prev.map((f, i) =>
          i === index
            ? {
                ...f,
                status: 'error' as const,
                errorMessage: error,
                progress: 0,
              }
            : f
        )
      );
      return;
    }

    let progress = 0;
    const interval = setInterval(() => {
      progress += 20;

      if (progress >= 100) {
        clearInterval(interval);
        setUploadedFiles(prev =>
          prev.map((f, i) =>
            i === index
              ? { ...f, status: 'success' as const, progress: 100 }
              : f
          )
        );
      } else {
        setUploadedFiles(prev =>
          prev.map((f, i) => (i === index ? { ...f, progress } : f))
        );
      }
    }, 300);
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const successfulFilesCount = uploadedFiles.filter(
      f => f.status === 'success'
    ).length;

    const newFilesArray = Array.from(files);
    const remainingSlots = maxFiles - successfulFilesCount;

    if (remainingSlots <= 0) {
      alert(`You can upload a maximum of ${maxFiles} files.`);
      return;
    }

    const limitedFiles = newFilesArray.slice(0, remainingSlots);

    const newFiles: UploadedFile[] = limitedFiles.map(file => ({
      file,
      progress: 0,
      status: 'uploading' as const,
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    newFiles.forEach((fileObj, index) => {
      simulateUpload(fileObj.file, uploadedFiles.length + index);
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAttach = () => {
    const successfulFiles = uploadedFiles
      .filter(f => f.status === 'success')
      .map(f => f.file);
    onAttach(successfulFiles);
    setUploadedFiles([]);
    onClose();
  };

  const handleCancel = () => {
    setUploadedFiles([]);
    onClose();
  };

  const isAttaching = uploadedFiles.some(f => f.status === 'uploading');
  const hasSuccessfulUploads = uploadedFiles.some(f => f.status === 'success');

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show close-modal"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        style={{ backgroundColor: 'transparent' }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: '600px' }}
        >
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title">{t('imagePicker.uploadAttach')}</h5>
                <p className="text-muted small mb-0">
                  {t('imagePicker.uploadText')}
                </p>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={handleCancel}
              />
            </div>

            <div className="modal-body">
              {/* Upload Area */}
              <div
                className={`border rounded p-4 text-center mb-3 ${
                  isDragging ? 'border-danger bg-light' : 'border-2'
                }`}
                style={{ borderStyle: 'dashed', cursor: 'pointer' }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="mb-3">
                  <i className="bi bi-cloud-upload"></i>
                </div>
                <p className="mb-1">
                  <span className="text-primary">
                    {t('imagePicker.clickUpload')}
                  </span>{' '}
                  {t('imagePicker.dragDrop')}
                </p>
                <p className="text-muted small mb-0">
                  {t('imagePicker.fileTypes')}
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp,image/heic,.png,.jpg,.jpeg,.webp,.heic"
                onChange={e => handleFileSelect(e.target.files)}
                style={{ display: 'none' }}
              />

              {/* Uploaded Files List */}
              {uploadedFiles.map((fileObj, index) => (
                <div
                  key={index}
                  className={`border rounded p-3 mb-2 ${
                    fileObj.status === 'error' ? 'border-danger' : ''
                  }`}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-medium">{fileObj.file.name}</span>
                      </div>
                      <div className="text-muted small">
                        {fileObj.status === 'uploading' &&
                          `${formatFileSize(fileObj.file.size * (fileObj.progress / 100))} of ${formatFileSize(fileObj.file.size)}`}
                        {fileObj.status === 'success' &&
                          `${formatFileSize(fileObj.file.size)} of ${formatFileSize(fileObj.file.size)}`}
                        {fileObj.status === 'error' &&
                          `${formatFileSize(0)} of ${formatFileSize(fileObj.file.size)}`}
                        {fileObj.status === 'uploading' && (
                          <span className="ms-2">
                            <i className="bi bi-arrow-up"></i>{' '}
                            {t('buttons.uploading')}
                          </span>
                        )}
                        {fileObj.status === 'success' && (
                          <span className="badge text-success border-0">
                            <i className="bi bi-check-circle me-1"></i>
                            {t('imagePicker.uploaded')}
                          </span>
                        )}
                        {fileObj.status === 'error' && (
                          <span className="badge text-primary border-0">
                            <i className="bi bi-x-circle me-1"></i>
                            {fileObj.errorMessage}
                          </span>
                        )}
                      </div>
                      {fileObj.status === 'error' && (
                        <button
                          className="btn btn-link text-primary p-0 small"
                          onClick={() => simulateUpload(fileObj.file, index)}
                        >
                          {t('buttons.again')}
                        </button>
                      )}
                    </div>
                    <button
                      className="btn btn-link text-muted p-0"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>

                  {/* Progress Bar */}
                  {(fileObj.status === 'uploading' ||
                    fileObj.status === 'success') && (
                    <div className="progress" style={{ height: '6px' }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${fileObj.progress}%` }}
                        aria-valuenow={fileObj.progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                  )}
                  {fileObj.status === 'uploading' && (
                    <div className="text-end small text-muted mt-1">
                      {fileObj.progress}%
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div
              className="modal-footer border-0 w-100 gap-2 justify-content-center mb-2"
              style={{ padding: 0 }}
            >
              <button
                type="button"
                style={{ width: '45%' }}
                className="btn btn-light text-dark fw-medium border border-dark rounded"
                onClick={handleCancel}
              >
                {t('buttons.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary text-light fw-medium border border-dark rounded"
                style={{ width: '45%' }}
                onClick={handleAttach}
                disabled={isAttaching || !hasSuccessfulUploads}
              >
                {isAttaching && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                {t('buttons.picture')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImagePicker;
